/**
 * PaymentMethodSection Component
 *
 * Inline Stripe Elements payment collection with billing details.
 * Used in ConfirmPaymentStep for first-time subscription activation.
 * Supports both credit cards and ACH bank accounts.
 */

import { memo, useEffect, useMemo, useRef } from 'react';
// Deep import (not the @features/organizations barrel) to avoid pulling the
// org page graph into the subscription checkout flow.
import { usePracticePlanDestination } from '@features/organizations/store/hooks';
import { CreditCard } from '@mui/icons-material';
import { Box, Card, CardContent, Typography } from '@mui/material';
import type { BillingDetails } from '@store/generated/subscriptions';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { StripePaymentForm, type StripePaymentFormHandle } from './StripePaymentForm';

// Initialize Stripe outside of component to avoid recreating on every render
const stripePromise = loadStripe((import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string) || '');

interface PaymentMethodSectionProps {
  /** SetupIntent client secret from backend */
  clientSecret: string;
  /** Callback when payment method is successfully confirmed */
  onPaymentMethodConfirmed: (paymentMethodId: string) => void;
  /** Callback when an error occurs during payment confirmation */
  onError?: (error: string) => void;
  /** Callback when PaymentElement iframe finishes loading */
  onElementLoaded?: () => void;
  /** Callback when payment form ready state changes (element loaded AND complete) */
  onReadyChange?: (isReady: boolean) => void;
  /** Optional existing billing details to pre-fill form */
  existingBillingDetails?: BillingDetails | null;
  /** Whether the parent is processing (to disable form) */
  isParentProcessing?: boolean;
}

/**
 * Inner form component that wraps StripePaymentForm and exposes confirm via window global
 */
function PaymentMethodFormWrapper({
  onPaymentMethodConfirmed,
  onError,
  onElementLoaded,
  onReadyChange,
  existingBillingDetails,
  isParentProcessing,
}: Omit<PaymentMethodSectionProps, 'clientSecret'>) {
  const formRef = useRef<StripePaymentFormHandle>(null);
  // Stripe returns here after a redirect-based confirmation (3DS / ACH). Land
  // the user on the practice surface that matches their org type rather than
  // the group-only Clinicians page.
  const planDestination = usePracticePlanDestination();

  // Expose confirm function to parent via window global pattern
  // This maintains backward compatibility with ConfirmPaymentStep
  useEffect(() => {
    const handleConfirm = async () => {
      if (!formRef.current) {
        throw new Error('Stripe payment form not initialized');
      }
      return formRef.current.confirmPayment();
    };

    (window as any).__stripeConfirmPayment = handleConfirm;

    return () => {
      delete (window as any).__stripeConfirmPayment;
    };
  }, []);

  return (
    <StripePaymentForm
      ref={formRef}
      onPaymentMethodConfirmed={onPaymentMethodConfirmed}
      onError={onError}
      onElementLoaded={onElementLoaded}
      onReadyChange={onReadyChange}
      existingBillingDetails={existingBillingDetails}
      isParentProcessing={isParentProcessing}
      returnUrl={`${window.location.origin}${planDestination.route}`}
      showInfoAlert
      showProcessingIndicator
      infoAlertText="A payment method is required to activate your subscription. You can choose between credit card or bank account (ACH)."
    />
  );
}

/**
 * Wrapper component that provides Stripe Elements context
 * Memoized to prevent unnecessary re-renders that could disrupt Stripe Elements
 */
export const PaymentMethodSection = memo(function PaymentMethodSection({
  clientSecret,
  onPaymentMethodConfirmed,
  onError,
  onElementLoaded,
  onReadyChange,
  existingBillingDetails,
  isParentProcessing,
}: PaymentMethodSectionProps) {
  // Memoize Elements options to prevent re-mounting Stripe Elements on every render
  const elementsOptions = useMemo(
    () => ({
      clientSecret,
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: '#1976d2',
        },
      },
    }),
    [clientSecret],
  );

  return (
    <Card
      sx={{
        border: '2px solid',
        borderColor: 'divider',
        borderRadius: '12px',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CreditCard sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            Payment Method
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your payment method and billing details below. We&apos;ll securely save this
          information for future billing.
        </Typography>

        <Elements stripe={stripePromise} options={elementsOptions}>
          <PaymentMethodFormWrapper
            onPaymentMethodConfirmed={onPaymentMethodConfirmed}
            onError={onError}
            onElementLoaded={onElementLoaded}
            onReadyChange={onReadyChange}
            existingBillingDetails={existingBillingDetails}
            isParentProcessing={isParentProcessing}
          />
        </Elements>
      </CardContent>
    </Card>
  );
});

/**
 * Helper function to trigger payment confirmation from parent component
 */
export async function confirmPaymentMethod(): Promise<void> {
  const confirmFn = (window as any).__stripeConfirmPayment;
  if (typeof confirmFn === 'function') {
    await confirmFn();
  } else {
    throw new Error('Stripe payment form not initialized');
  }
}
